import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  GraduationCap,
  BookOpen,
  TrendingUp,
  TrendingDown,
  Building2,
  MapPin,
  Award,
  AlertTriangle
} from 'lucide-react';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import HeroCarousel from '../../../../components/carousel/HeroCarousel';
import KVSLoader from '../../../../components/loaders/KVSLoader';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import img1 from '../../../../assets/attendenceCarousel.png';

// import { TrendLineChart, ComparisonBarChart, DonutChart } from '../../components/charts';
import { attendanceApi } from '../../services/api';
import { DateRangePicker } from '../../components/analytics';
import { formatDateStr } from '../../../../shared/utils/dateUtils';

// Map class number to display name
const getClassName = (classNum: number): string => {
  const classNames: { [key: number]: string } = {
    [-3]: 'Balvatika I',
    [-2]: 'Balvatika II',
    [-1]: 'Balvatika III',
    1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V',
    6: 'VI', 7: 'VII', 8: 'VIII', 9: 'IX', 10: 'X',
    11: 'XI', 12: 'XII'
  };
  return classNames[classNum] || `Class ${classNum}`;
};

// Get class stage/category
const getClassStage = (classNum: number): string => {
  if (classNum <= 2) return 'Foundational';
  if (classNum <= 5) return 'Preparatory';
  if (classNum <= 8) return 'Middle';
  return 'Secondary';
};

interface ClassDetailData {
  class: number;
  totalStudents: number;
  presentStudents: number;
  attendance: number;
  boys: number;
  girls: number;
  boysPresent: number;
  girlsPresent: number;
  boysAttendance: number;
  girlsAttendance: number;
}

interface SchoolClassData {
  kvId: number;
  kvCode: string;
  kvName: string;
  roId: number;
  roName: string;
  totalStudents: number;
  presentStudents: number;
  attendance: number;
}

// Transform API response to school-wise data
const transformSchoolWiseData = (apiData: any): SchoolClassData[] => {
  if (!apiData?.success || !apiData?.data || apiData.data.length === 0) {
    return [];
  }

  return apiData.data.map((school: any) => {
    let totalStudents = 0;
    let totalPresent = 0;

    // Handle empty classes array or missing data
    if (Array.isArray(school.classes) && school.classes.length > 0) {
      school.classes.forEach((cls: any) => {
        if (Array.isArray(cls.sections) && cls.sections.length > 0) {
          cls.sections.forEach((section: any) => {
            totalStudents += Number(section.totalEnrollment) || 0;
            totalPresent += Number(section.totalPresent) || 0;
          });
        }
      });
    }

    const attendance = totalStudents > 0
      ? Math.round((totalPresent / totalStudents) * 1000) / 10
      : 0;

    return {
      kvId: parseInt(school.kvID) || 0,
      kvCode: String(school.schoolCode) || '',
      kvName: school.schoolName || '',
      roId: 0,
      roName: school.roName || '',
      totalStudents,
      presentStudents: totalPresent,
      attendance
    };
  });
};

// Calculate overall class data from school-wise response
const calculateClassData = (apiData: any, classNum: number): ClassDetailData | null => {
  if (!apiData?.success || !apiData?.data) {
    return null;
  }

  const data = apiData.data;

  const totalStudents = Number(data.totalEnrollment) || 0;
  const totalPresent = Number(data.totalPresent) || 0;
  const totalBoys = Number(data.totaboysEnrolled) || 0;
  const totalGirls = Number(data.totalgirlsEnrolled) || 0;
  const totalBoysPresent = Number(data.totalBoysPresent) || 0;
  const totalGirlsPresent = Number(data.totalGirlsPresent) || 0;

  const attendance = Number(data.attendancePercentage) || 0;
  const boysAttendance = Number(data.boysPresentPercentage) || 0;
  const girlsAttendance = Number(data.girlsPresentPercentage) || 0;

  return {
    class: classNum,
    totalStudents,
    presentStudents: totalPresent,
    attendance,
    boys: totalBoys,
    girls: totalGirls,
    boysPresent: totalBoysPresent,
    girlsPresent: totalGirlsPresent,
    boysAttendance,
    girlsAttendance
  };
};

const StudentClassDetail: React.FC = () => {
  const navigate = useNavigate();
  const { classId } = useParams<{ classId: string }>();
  const carouselImages = [img1];

  const classNum = parseInt(classId || '1');
  const className = getClassName(classNum);
  const classStage = getClassStage(classNum);

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [classData, setClassData] = useState<ClassDetailData | null>(null);
  const [schoolsData, setSchoolsData] = useState<SchoolClassData[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [genderFilter, setGenderFilter] = useState<'all' | 'boys' | 'girls'>('all');
  const [sortBy, setSortBy] = useState<'attendance' | 'students' | 'name'>('attendance');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchParams, setSearchParams] = useSearchParams();
  const fromParam = searchParams.get('fromDate');
  const toParam = searchParams.get('toDate');

  const [fromDate, setFromDate] = useState<string>(() => {
    if (fromParam) return fromParam;
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return formatDateStr(d);
  });
  const [toDate, setToDate] = useState<string>(() => {
    if (toParam) return toParam;
    return formatDateStr(new Date());
  });





  // Load data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch both total class data and school-wise section attendance in parallel
      const [totalClassResponse, schoolsResponse] = await Promise.all([
        attendanceApi.getTotalClassWiseSectionData(classNum, fromDate, toDate),
        attendanceApi.getSchoolWiseSectionAttendance(classNum, currentPage, fromDate, toDate)
      ]);

      // console.log('Total Class API Response:', totalClassResponse);
      // console.log('Schools API Response:', schoolsResponse);
      // console.log('Pagination:', schoolsResponse?.pagination);

      // Set pagination info
      if (schoolsResponse?.pagination) {
        setTotalPages(schoolsResponse.pagination.totalPages || 1);
      }

      // Transform and set school data
      const transformedSchools = transformSchoolWiseData(schoolsResponse);
      // console.log('Transformed Schools:', transformedSchools);
      setSchoolsData(transformedSchools);

      // Calculate and set overall class data from total API
      const calculatedClassData = calculateClassData(totalClassResponse, classNum);
      // console.log('Calculated Class Data:', calculatedClassData);
      setClassData(calculatedClassData);

    } catch (err) {
      // console.error('Error loading class detail:', err);
    } finally {
      setIsLoading(false);
    }
  }, [classNum, currentPage, fromDate, toDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Sorted and filtered schools
  const sortedSchools = React.useMemo(() => {
    let filtered = [...schoolsData];

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'attendance') {
        return sortOrder === 'desc' ? b.attendance - a.attendance : a.attendance - b.attendance;
      } else if (sortBy === 'students') {
        return sortOrder === 'desc' ? b.totalStudents - a.totalStudents : a.totalStudents - b.totalStudents;
      } else {
        return sortOrder === 'desc'
          ? b.kvName.localeCompare(a.kvName)
          : a.kvName.localeCompare(b.kvName);
      }
    });

    return filtered;
  }, [schoolsData, sortBy, sortOrder]);

  // Stats calculation
  const stats = React.useMemo(() => {
    if (!classData) return null;

    const avgAttendance = classData.attendance;
    const absentStudents = classData.totalStudents - classData.presentStudents;
    const absenteeRate = (absentStudents / classData.totalStudents) * 100;

    return {
      avgAttendance,
      absentStudents,
      absenteeRate,
      genderGap: Math.abs(classData.boysAttendance - classData.girlsAttendance)
    };
  }, [classData]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="flex-grow flex flex-col items-center justify-center">
          <KVSLoader size={80} />
          <span className="mt-4 text-gray-600">Loading class details...</span>
        </div>
        <Footer />
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="flex-grow flex flex-col items-center justify-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
          <span className="text-gray-600">Class data not found</span>
          <button
            onClick={() => navigate('/attendance/student/analytics?tab=classes')}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Back to Classes
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4 pt-2 sm:pt-3">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/attendance/student/analytics?tab=classes&fromDate=${fromDate}&toDate=${toDate}`)}
              className="p-2 hover:bg-white rounded-lg transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <BookOpen className="w-7 h-7 text-purple-600" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Class {className} - Student Attendance
                  </h1>
                  <p className="text-sm text-gray-600 mt-1">
                    {classStage} Stage • Detailed Analytics
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* DateRangePicker */}
            <DateRangePicker
              value={{ fromDate, toDate }}
              onSubmit={(range) => {
                if (range.fromDate && range.toDate) {
                  setFromDate(range.fromDate);
                  setToDate(range.toDate);
                  setSearchParams(prev => {
                    const next = new URLSearchParams(prev);
                    next.set('fromDate', range.fromDate!);
                    next.set('toDate', range.toDate!);
                    return next;
                  });
                }
              }}
              disabled={isLoading}
            />

            {/* Gender Filter */}
            <div className="flex items-center gap-2 bg-white rounded-lg p-1 border border-gray-200">
              <button
                onClick={() => setGenderFilter('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${genderFilter === 'all'
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                  }`}
              >
                All
              </button>
              <button
                onClick={() => setGenderFilter('boys')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${genderFilter === 'boys'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                  }`}
              >
                Boys
              </button>
              <button
                onClick={() => setGenderFilter('girls')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${genderFilter === 'girls'
                    ? 'bg-pink-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                  }`}
              >
                Girls
              </button>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Enrolled Students</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {genderFilter === 'all'
                    ? classData.totalStudents.toLocaleString()
                    : genderFilter === 'boys'
                      ? classData.boys.toLocaleString()
                      : classData.girls.toLocaleString()
                  }
                </p>
              </div>
              <Users className="w-10 h-10 text-purple-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Present Today</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {genderFilter === 'all'
                    ? classData.presentStudents.toLocaleString()
                    : genderFilter === 'boys'
                      ? classData.boysPresent.toLocaleString()
                      : classData.girlsPresent.toLocaleString()
                  }
                </p>
              </div>
              <GraduationCap className="w-10 h-10 text-green-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Attendance Rate</p>
                <p className={`text-2xl font-bold mt-1 ${stats && stats.avgAttendance >= 85 ? 'text-green-600' :
                    stats && stats.avgAttendance >= 70 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                  {genderFilter === 'all'
                    ? classData.attendance.toFixed(1)
                    : genderFilter === 'boys'
                      ? classData.boysAttendance.toFixed(1)
                      : classData.girlsAttendance.toFixed(1)
                  }%
                </p>
              </div>
              <TrendingUp className="w-10 h-10 text-blue-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Absent Students</p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                  {genderFilter === 'all'
                    ? stats?.absentStudents.toLocaleString()
                    : genderFilter === 'boys'
                      ? (classData.boys - classData.boysPresent).toLocaleString()
                      : (classData.girls - classData.girlsPresent).toLocaleString()
                  }
                </p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* Gender Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gender Distribution */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Gender Distribution</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-900">Boys</span>
                </div>
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  {classData.boys.toLocaleString()}
                </div>
                <div className="text-sm text-blue-700">
                  {((classData.boys / classData.totalStudents) * 100).toFixed(1)}% of class
                </div>
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <div className="text-sm text-blue-600">Attendance</div>
                  <div className="text-xl font-bold text-blue-700">
                    {classData.boysAttendance.toFixed(1)}%
                  </div>
                </div>
              </div>

              <div className="p-4 bg-pink-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-pink-600" />
                  <span className="font-medium text-pink-900">Girls</span>
                </div>
                <div className="text-2xl font-bold text-pink-600 mb-1">
                  {classData.girls.toLocaleString()}
                </div>
                <div className="text-sm text-pink-700">
                  {((classData.girls / classData.totalStudents) * 100).toFixed(1)}% of class
                </div>
                <div className="mt-3 pt-3 border-t border-pink-200">
                  <div className="text-sm text-pink-600">Attendance</div>
                  <div className="text-xl font-bold text-pink-700">
                    {classData.girlsAttendance.toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>

            {stats && stats.genderGap > 5 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium text-amber-900">Gender Gap Alert</div>
                    <div className="text-xs text-amber-700 mt-1">
                      {stats.genderGap.toFixed(1)}% difference in attendance between boys and girls
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Performance Indicators */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Indicators</h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Overall Attendance</span>
                  <span className="text-sm font-medium text-gray-900">
                    {classData.attendance.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${classData.attendance >= 85 ? 'bg-green-600' :
                        classData.attendance >= 70 ? 'bg-yellow-600' : 'bg-red-600'
                      }`}
                    style={{ width: `${Math.min(classData.attendance, 100)}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Boys Attendance</span>
                  <span className="text-sm font-medium text-gray-900">
                    {classData.boysAttendance.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full bg-blue-600"
                    style={{ width: `${Math.min(classData.boysAttendance, 100)}%` }}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Girls Attendance</span>
                  <span className="text-sm font-medium text-gray-900">
                    {classData.girlsAttendance.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full bg-pink-600"
                    style={{ width: `${Math.min(classData.girlsAttendance, 100)}%` }}
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-600">Target</div>
                    <div className="text-lg font-bold text-gray-900">85%</div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-600">Gap</div>
                    <div className={`text-lg font-bold ${classData.attendance >= 85 ? 'text-green-600' : 'text-red-600'
                      }`}>
                      {classData.attendance >= 85
                        ? `+${(classData.attendance - 85).toFixed(1)}%`
                        : `${(classData.attendance - 85).toFixed(1)}%`
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* School-wise Performance */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  School-wise Performance
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Class {className} attendance across {sortedSchools.length} schools
                </p>
              </div>

              {/* Sort Controls */}
              <div className="flex items-center gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                >
                  <option value="attendance">Sort by Attendance</option>
                  <option value="students">Sort by Students</option>
                  <option value="name">Sort by Name</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  {sortOrder === 'asc' ? (
                    <TrendingUp className="w-5 h-5 text-gray-600" />
                  ) : (
                    <TrendingDown className="w-5 h-5 text-gray-600" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Schools Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Rank</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">School</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Region</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-600">Students</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-600">Present</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-600">Attendance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedSchools.map((school, idx) => (
                  <tr key={school.kvId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {idx < 3 && (
                          <Award className={`w-4 h-4 ${idx === 0 ? 'text-yellow-500' :
                              idx === 1 ? 'text-gray-400' : 'text-amber-600'
                            }`} />
                        )}
                        <span className="text-sm font-medium text-gray-900">#{idx + 1}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{school.kvName}</div>
                          <div className="text-xs text-gray-500">{school.kvCode}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <MapPin className="w-3.5 h-3.5" />
                        {school.roName}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm text-gray-900">{school.totalStudents}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-medium text-green-600">
                        {school.presentStudents}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-20 bg-gray-200 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${school.attendance >= 85 ? 'bg-green-600' :
                                school.attendance >= 70 ? 'bg-yellow-600' : 'bg-red-600'
                              }`}
                            style={{ width: `${Math.min(school.attendance, 100)}%` }}
                          />
                        </div>
                        <span className={`text-sm font-medium ${school.attendance >= 85 ? 'text-green-600' :
                            school.attendance >= 70 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                          {school.attendance.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StudentClassDetail;
